import {
  deleteEnrollment,
  deleteSeason,
  fetchEnrollments,
  fetchProducts,
  fetchSeasons,
  saveEnrollment,
  saveSeason,
} from "../lib/api.js";
import { formatCurrency, formatDate, parseAmount, toLocalISODate } from "../lib/format.js";
import { confirmDialog, escapeHtml, openModal, showError, wireAmountInputs } from "../lib/ui.js";

/**
 * Colonia y otros productos de temporada: quién se inscribió, quién pagó y
 * quién debe. Pantalla exclusiva de admin — las tablas seasons y enrollments
 * no tienen ninguna política para el rol empleado.
 */
export async function renderSeasons(container) {
  container.innerHTML = `<h2>Colonia e inscripciones</h2><p>Cargando…</p>`;

  try {
    const [seasons, products] = await Promise.all([fetchSeasons(), fetchProducts()]);
    const reload = () => renderSeasons(container);

    if (seasons.length === 0) {
      container.innerHTML = `
        <div id="view">
          <h2>Colonia e inscripciones</h2>
          <div class="panel">
            <p>Todavía no hay ninguna temporada cargada.</p>
            <p class="hint">
              Una temporada es un período de colonia: tiene sus fechas de inscripción
              (cuando entran las anotaciones y los pagos) y sus fechas de funcionamiento.
            </p>
            <button class="btn" id="new-season">+ Nueva temporada</button>
          </div>
        </div>`;
      container.querySelector("#new-season").addEventListener("click", () => openSeasonModal(products, null, reload));
      return;
    }

    let currentSeasonId = seasons[0].id;
    let filterState = "todos"; // todos | pagado | debe

    const drawEnrollments = async () => {
      const season = seasons.find((s) => s.id === currentSeasonId);
      const enrollments = await fetchEnrollments(currentSeasonId);

      const withBalance = enrollments.map((e) => ({
        ...e,
        balance: Number(e.amount_due) - Number(e.amount_paid),
      }));

      const visible = withBalance.filter((e) => {
        if (filterState === "pagado") return e.balance <= 0;
        if (filterState === "debe") return e.balance > 0;
        return true;
      });

      const totalDue = withBalance.reduce((s, e) => s + Number(e.amount_due), 0);
      const totalPaid = withBalance.reduce((s, e) => s + Number(e.amount_paid), 0);
      const owing = withBalance.filter((e) => e.balance > 0);

      const panel = container.querySelector("#enrollments-panel");
      panel.innerHTML = `
        <div class="cards-grid">
          <div class="card"><div class="label">Inscriptos</div><div class="value">${withBalance.length}</div></div>
          <div class="card"><div class="label">Cobrado</div><div class="value positive">${formatCurrency(totalPaid)}</div></div>
          <div class="card"><div class="label">Por cobrar</div><div class="value ${totalDue - totalPaid > 0 ? "negative" : ""}">${formatCurrency(totalDue - totalPaid)}</div></div>
          <div class="card"><div class="label">Deben</div><div class="value">${owing.length}</div></div>
        </div>

        <div class="panel">
          <div class="toolbar">
            <div class="filters" style="margin: 0">
              <select id="f-state">
                <option value="todos"${filterState === "todos" ? " selected" : ""}>Todos</option>
                <option value="debe"${filterState === "debe" ? " selected" : ""}>Sólo los que deben</option>
                <option value="pagado"${filterState === "pagado" ? " selected" : ""}>Sólo los que pagaron</option>
              </select>
            </div>
            <button class="btn" id="new-enrollment">+ Inscribir chico</button>
          </div>

          ${
            visible.length === 0
              ? "<p>No hay inscripciones que coincidan.</p>"
              : `<div class="table-wrap"><table class="table-cards">
                  <thead><tr><th>Chico/a</th><th>Adulto</th><th>Contacto</th><th>A pagar</th><th>Pagado</th><th>Saldo</th><th></th></tr></thead>
                  <tbody>
                    ${visible
                      .map(
                        (e) => `<tr>
                          <td data-label="Chico/a">${escapeHtml(e.child_name)}</td>
                          <td data-label="Adulto">${escapeHtml(e.guardian_name ?? "—")}</td>
                          <td data-label="Contacto">${escapeHtml(e.contact ?? "—")}</td>
                          <td data-label="A pagar">${formatCurrency(e.amount_due)}</td>
                          <td data-label="Pagado">${formatCurrency(e.amount_paid)}</td>
                          <td data-label="Saldo" class="${e.balance > 0 ? "value negative" : "value positive"}" style="font-size: 14px">
                            ${e.balance > 0 ? formatCurrency(e.balance) : "al día"}
                          </td>
                          <td data-label="">
                            <div class="row-actions">
                              ${e.balance > 0 ? `<button class="icon-btn" data-pay="${escapeHtml(e.id)}" data-balance="${e.balance}">Registrar pago</button>` : ""}
                              <button class="icon-btn" data-edit-enrollment="${escapeHtml(e.id)}">Editar</button>
                              <button class="icon-btn danger" data-delete-enrollment="${escapeHtml(e.id)}">Borrar</button>
                            </div>
                          </td>
                        </tr>`
                      )
                      .join("")}
                  </tbody>
                </table></div>`
          }
        </div>
      `;

      panel.querySelector("#f-state").addEventListener("change", (e) => {
        filterState = e.target.value;
        drawEnrollments();
      });

      panel.querySelector("#new-enrollment").addEventListener("click", () =>
        openEnrollmentModal(season, null, drawEnrollments)
      );

      panel.addEventListener("click", async (e) => {
        const payId = e.target.dataset?.pay;
        const editId = e.target.dataset?.editEnrollment;
        const deleteId = e.target.dataset?.deleteEnrollment;

        if (payId) {
          const balance = Number(e.target.dataset.balance);
          const input = window.prompt(`¿Cuánto pagó? (saldo: ${formatCurrency(balance)})`, balance);
          if (input === null) return;
          const amount = parseAmount(input);
          if (!Number.isFinite(amount) || amount <= 0) return;

          const enrollment = withBalance.find((x) => x.id === payId);
          const newPaid = Number(enrollment.amount_paid) + amount;
          await saveEnrollment(
            {
              amount_paid: newPaid,
              paid_at: newPaid >= Number(enrollment.amount_due) ? toLocalISODate() : null,
            },
            payId
          );
          drawEnrollments();
        }

        if (editId) {
          openEnrollmentModal(season, withBalance.find((x) => x.id === editId), drawEnrollments);
        }

        if (deleteId && confirmDialog("¿Borrar esta inscripción?")) {
          await deleteEnrollment(deleteId);
          drawEnrollments();
        }
      });
    };

    container.innerHTML = `
      <div id="view">
        <h2>Colonia e inscripciones</h2>

        <div class="toolbar">
          <div class="filters" style="margin: 0">
            <select id="season-select">
              ${seasons
                .map(
                  (s) => `<option value="${escapeHtml(s.id)}">${escapeHtml(s.name)} · inscripción ${formatDate(s.enrollment_start)}–${formatDate(s.enrollment_end)}</option>`
                )
                .join("")}
            </select>
          </div>
          <div class="row-actions">
            <button class="icon-btn" id="edit-season">Editar temporada</button>
            <button class="btn" id="new-season">+ Nueva temporada</button>
          </div>
        </div>

        <div id="season-info" class="notice"></div>
        <div id="enrollments-panel"></div>
      </div>
    `;

    const drawSeasonInfo = () => {
      const s = seasons.find((x) => x.id === currentSeasonId);
      const today = toLocalISODate();
      const inEnrollment = today >= s.enrollment_start && today <= s.enrollment_end;
      const running = today >= s.season_start && today <= s.season_end;

      container.querySelector("#season-info").innerHTML = `
        <span><strong>${escapeHtml(s.name)}</strong></span>
        <span>Inscripción: ${formatDate(s.enrollment_start)} → ${formatDate(s.enrollment_end)}</span>
        <span>Colonia: ${formatDate(s.season_start)} → ${formatDate(s.season_end)}</span>
        <span>Precio por chico: ${formatCurrency(s.price_per_child)}</span>
        ${inEnrollment ? '<span class="badge" style="background: var(--primary)">inscripción abierta</span>' : ""}
        ${running ? '<span class="badge" style="background: var(--success)">en curso</span>' : ""}
      `;
    };

    container.querySelector("#season-select").addEventListener("change", (e) => {
      currentSeasonId = e.target.value;
      drawSeasonInfo();
      drawEnrollments();
    });

    container.querySelector("#new-season").addEventListener("click", () => openSeasonModal(products, null, reload));
    container.querySelector("#edit-season").addEventListener("click", () =>
      openSeasonModal(products, seasons.find((s) => s.id === currentSeasonId), reload)
    );

    drawSeasonInfo();
    await drawEnrollments();
  } catch (error) {
    showError(container, error);
  }
}

function openSeasonModal(products, season, onSaved) {
  const modal = openModal(`
    <button class="modal-close" type="button" data-close>✕</button>
    <h3>${season ? "Editar temporada" : "Nueva temporada"}</h3>
    <form id="season-form">
      <div class="form-grid">
        <div class="field full">
          <label for="s-name">Nombre</label>
          <input type="text" id="s-name" name="name" required placeholder="Colonia de verano 2027"
                 value="${escapeHtml(season?.name ?? "")}" />
        </div>
        <div class="field full">
          <label for="s-product">Producto</label>
          <select id="s-product" name="product_id" required>
            ${products
              .filter((p) => p.active !== false || season?.product_id === p.id)
              .map(
                (p) => `<option value="${escapeHtml(p.id)}"${season?.product_id === p.id ? " selected" : ""}>${escapeHtml(p.name)}</option>`
              )
              .join("")}
          </select>
        </div>
        <div class="field">
          <label for="s-es">Inscripción desde</label>
          <input type="date" id="s-es" name="enrollment_start" required value="${escapeHtml(season?.enrollment_start ?? "")}" />
        </div>
        <div class="field">
          <label for="s-ee">Inscripción hasta</label>
          <input type="date" id="s-ee" name="enrollment_end" required value="${escapeHtml(season?.enrollment_end ?? "")}" />
        </div>
        <div class="field">
          <label for="s-ss">Colonia desde</label>
          <input type="date" id="s-ss" name="season_start" required value="${escapeHtml(season?.season_start ?? "")}" />
        </div>
        <div class="field">
          <label for="s-se">Colonia hasta</label>
          <input type="date" id="s-se" name="season_end" required value="${escapeHtml(season?.season_end ?? "")}" />
        </div>
        <div class="field full">
          <label for="s-price">Precio por chico</label>
          <input type="text" inputmode="decimal" id="s-price" name="price_per_child" data-amount
                 value="${season?.price_per_child ?? 0}" />
        </div>
      </div>
      <p class="error-text" id="season-error" hidden></p>
      <div class="modal-actions">
        ${season ? '<button type="button" class="btn danger" id="delete-season">Borrar temporada</button>' : ""}
        <button type="button" class="btn secondary" data-close>Cancelar</button>
        <button type="submit" class="btn">Guardar</button>
      </div>
    </form>
  `);

  modal.querySelectorAll("[data-close]").forEach((b) => b.addEventListener("click", () => modal.close()));
  wireAmountInputs(modal);

  // Borrar una temporada arrastra sus inscripciones en cascada, con lo cobrado
  // y lo que falta cobrar. El aviso lo dice con el número concreto: sin eso,
  // "¿borrar?" no alcanza para decidir.
  modal.querySelector("#delete-season")?.addEventListener("click", async () => {
    const inscriptos = await fetchEnrollments(season.id);
    const cobrado = inscriptos.reduce((t, e) => t + (Number(e.amount_paid) || 0), 0);

    const aviso =
      inscriptos.length === 0
        ? `¿Borrar la temporada "${season.name}"? No tiene inscripciones.`
        : `La temporada "${season.name}" tiene ${inscriptos.length} inscripción(es), ` +
          `con ${formatCurrency(cobrado)} ya cobrados.\n\n` +
          `Al borrarla se borran TODAS esas inscripciones y sus pagos. No se puede deshacer.\n\n` +
          `¿Borrar igual?`;

    if (!confirmDialog(aviso)) return;
    await deleteSeason(season.id);
    modal.close();
    onSaved?.();
  });

  modal.querySelector("#season-form").addEventListener("submit", async (e) => {
    e.preventDefault();
    const form = e.target;
    const errorEl = modal.querySelector("#season-error");

    try {
      await saveSeason(
        {
          name: form.name.value.trim(),
          product_id: form.product_id.value,
          enrollment_start: form.enrollment_start.value,
          enrollment_end: form.enrollment_end.value,
          season_start: form.season_start.value,
          season_end: form.season_end.value,
          price_per_child: parseAmount(form.price_per_child.value),
        },
        season?.id ?? null
      );
      modal.close();
      onSaved?.();
    } catch (error) {
      errorEl.textContent = error.message;
      errorEl.hidden = false;
    }
  });
}

function openEnrollmentModal(season, enrollment, onSaved) {
  const modal = openModal(`
    <button class="modal-close" type="button" data-close>✕</button>
    <h3>${enrollment ? "Editar inscripción" : "Inscribir chico/a"}</h3>
    <form id="enrollment-form">
      <div class="form-grid">
        <div class="field full">
          <label for="e-child">Nombre del chico/a</label>
          <input type="text" id="e-child" name="child_name" required value="${escapeHtml(enrollment?.child_name ?? "")}" />
        </div>
        <div class="field">
          <label for="e-guardian">Adulto responsable</label>
          <input type="text" id="e-guardian" name="guardian_name" value="${escapeHtml(enrollment?.guardian_name ?? "")}" />
        </div>
        <div class="field">
          <label for="e-contact">Contacto</label>
          <input type="text" id="e-contact" name="contact" value="${escapeHtml(enrollment?.contact ?? "")}" />
        </div>
        <div class="field">
          <label for="e-due">A pagar</label>
          <input type="text" inputmode="decimal" id="e-due" name="amount_due" data-amount
                 value="${enrollment?.amount_due ?? season.price_per_child}" />
        </div>
        <div class="field">
          <label for="e-paid">Pagado</label>
          <input type="text" inputmode="decimal" id="e-paid" name="amount_paid" data-amount
                 value="${enrollment?.amount_paid ?? 0}" />
        </div>
        <div class="field full">
          <label for="e-notes">Notas</label>
          <textarea id="e-notes" name="notes" rows="2">${escapeHtml(enrollment?.notes ?? "")}</textarea>
        </div>
      </div>
      <p class="error-text" id="enrollment-error" hidden></p>
      <div class="modal-actions">
        <button type="button" class="btn secondary" data-close>Cancelar</button>
        <button type="submit" class="btn">Guardar</button>
      </div>
    </form>
  `);

  modal.querySelectorAll("[data-close]").forEach((b) => b.addEventListener("click", () => modal.close()));
  wireAmountInputs(modal);

  modal.querySelector("#enrollment-form").addEventListener("submit", async (e) => {
    e.preventDefault();
    const form = e.target;
    const errorEl = modal.querySelector("#enrollment-error");
    const due = parseAmount(form.amount_due.value);
    const paid = parseAmount(form.amount_paid.value);

    try {
      await saveEnrollment(
        {
          season_id: season.id,
          child_name: form.child_name.value.trim(),
          guardian_name: form.guardian_name.value.trim() || null,
          contact: form.contact.value.trim() || null,
          amount_due: due,
          amount_paid: paid,
          paid_at: paid >= due && due > 0 ? toLocalISODate() : null,
          notes: form.notes.value.trim() || null,
        },
        enrollment?.id ?? null
      );
      modal.close();
      onSaved?.();
    } catch (error) {
      errorEl.textContent = error.message;
      errorEl.hidden = false;
    }
  });
}
